import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, CircularProgress, Chip } from '@mui/material';
import { useSelector } from 'react-redux';
import { useAuthToken } from '../../TokenContext';
import { useRisks, useSignals, useDashboardMetrics } from './useCPData';
import { useDoraSummary } from './useDoraSummary';
import EpicsCountGraph from '../EpicsCountGraph';
import './ArtemyStyles.css';

// ── Reusable mini components ─────────────────────────────────────────────
function MetricCard({ label, value, unit = '', delta, deltaUp, color = 'var(--artemy-text-primary)', spark }) {
  return (
    <div className="artemy-metric-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-secondary)', letterSpacing: '.3px' }}>{label}</div>
        {delta && (
          <div style={{
            fontFamily: 'var(--artemy-font-mono)', fontSize: 11, padding: '2px 8px', borderRadius: 10,
            background: deltaUp ? 'rgba(0,212,170,.1)' : 'rgba(255,77,106,.1)',
            color: deltaUp ? 'var(--artemy-green)' : 'var(--artemy-red)',
          }}>{delta}</div>
        )}
      </div>
      <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 26, fontWeight: 700, color, lineHeight: 1, marginBottom: 2 }}>
        {value}<span style={{ fontSize: 12, color: 'var(--artemy-text-dim)', marginLeft: 2 }}>{unit}</span>
      </div>
      {spark && (
        <div style={{ marginTop: 8, height: 30 }}>
          <Sparkline values={spark} color={color} />
        </div>
      )}
    </div>
  );
}

function Sparkline({ values, color }) {
  const mx = Math.max(...values), mn = Math.min(...values), r = mx - mn || 1;
  const pts = values.map((v, i) => `${i * (120 / (values.length - 1))},${28 - ((v - mn) / r) * 22}`).join(' ');
  const up = values[values.length - 1] >= values[0];
  const strokeColor = color || (up ? 'var(--artemy-green)' : 'var(--artemy-accent)');
  return (
    <svg viewBox="0 0 120 30" width="100%" height="30" style={{ overflow: 'visible' }}>
      <polyline points={pts} fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ConfidenceRing({ score }) {
  const circumference = 2 * Math.PI * 45;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? 'var(--artemy-green)' : score >= 60 ? 'var(--artemy-yellow)' : 'var(--artemy-red)';
  const label = score >= 80 ? 'GREEN — Confident' : score >= 60 ? 'YELLOW — Caution' : 'RED — At Risk';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 18 }}>
      <div style={{ position: 'relative', width: 88, height: 88, flexShrink: 0 }}>
        <svg viewBox="0 0 100 100" width="88" height="88" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="50" cy="50" r="45" fill="none" stroke="var(--artemy-border)" strokeWidth="8" />
          <circle cx="50" cy="50" r="45" fill="none" stroke={color} strokeWidth="8"
            strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 1s ease' }} />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{score}</div>
          <div style={{ fontSize: 10, color: 'var(--artemy-text-dim)' }}>/ 100</div>
        </div>
      </div>
      <div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 20, marginBottom: 7, fontSize: 11.5, fontWeight: 500, background: `${color}1a`, color, border: `1px solid ${color}40` }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
          {label}
        </div>
        <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
          {score >= 80 ? 'Delivery on track' : score >= 60 ? 'Moderate risk detected' : 'Immediate action required'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--artemy-text-secondary)', lineHeight: 1.5 }}>
          1 high-risk item needs attention. 2 teams under sprint pressure.
        </div>
      </div>
    </div>
  );
}

function StatusRing({ color, size = 20, stroke = 4 }) {
  const r = (size / 2) - (stroke / 2);
  const circum = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--artemy-border)" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke} 
              strokeDasharray={circum} strokeDashoffset={circum * 0.3} strokeLinecap="round" 
              transform={`rotate(-90 ${size/2} ${size/2})`} />
    </svg>
  );
}

function DimBar({ label, pct, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 11.5 }}>
      <div style={{ flex: 1, color: 'var(--artemy-text-secondary)' }}>{label}</div>
      <div style={{ width: 86, height: 4, background: 'var(--artemy-bg-elevated)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4 }} />
      </div>
      <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, width: 32, textAlign: 'right', color }}>{pct}%</div>
    </div>
  );
}

function SeverityChip({ severity }) {
  const map = { Critical: ['var(--artemy-red)', 'rgba(255,77,106,.1)'], High: ['var(--artemy-red)', 'rgba(255,77,106,.1)'], Medium: ['var(--artemy-yellow)', 'rgba(245,166,35,.1)'], Low: ['var(--artemy-accent)', 'rgba(61,127,255,.1)'], Info: ['var(--artemy-accent)', 'rgba(61,127,255,.1)'] };
  const [color, bg] = map[severity] || ['var(--artemy-text-secondary)', 'rgba(139,150,180,.1)'];
  return <span style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10, color, background: bg, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>{severity}</span>;
}

// ── Main Dashboard ───────────────────────────────────────────────────────
export default function ControlPlaneDashboard() {
  const history = useHistory();
  const { user } = useSelector(state => state.auth);
  const { risks, loading: risksLoading } = useRisks();
  const { signals } = useSignals();
  const { dora } = useDoraSummary();
  const { metrics, loading: metricsLoading } = useDashboardMetrics();

  const confidenceScore = useMemo(() => {
    if (!dora) return 80;
    const doraHealth = dora.health || 80;
    const agentReliability = 95;
    const intentAlignment = 74;
    const dependencyRisk = 38;
    const capacityUtil = 81;
    return Math.round(0.3 * doraHealth + 0.2 * agentReliability + 0.2 * intentAlignment + 0.2 * (100 - dependencyRisk) + 0.1 * capacityUtil);
  }, [dora]);

  const topRisks = risks.slice(0, 5);
  const liveSignals = signals.slice(0, 4);

  return (
    <div className="artemy-fade-up">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
        <div>
          <div className="artemy-page-title">Delivery Confidence Control Plane</div>
          <div className="artemy-page-sub">Portfolio snapshot · 4 programs · 14 teams · Updated 2 min ago</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="artemy-btn artemy-btn-ghost">⬇ Export Brief</button>
          <button className="artemy-btn artemy-btn-primary" onClick={() => history.push('/control-plane/policies')}>+ New Policy</button>
        </div>
      </div>

      {/* Live ticker */}
      <div style={{ background: 'var(--artemy-bg-elevated)', border: '1px solid var(--artemy-border)', borderRadius: 10, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10, letterSpacing: '1.2px', color: 'var(--artemy-text-dim)', textTransform: 'uppercase', flexShrink: 0 }}>Live</div>
        <div style={{ width: 1, height: 14, background: 'var(--artemy-border)', flexShrink: 0 }} />
        <div style={{ display: 'flex', gap: 22, overflow: 'hidden', flex: 1 }}>
          {liveSignals.length > 0 ? liveSignals.map((sig, i) => {
            const c = sig.severity === 'High' ? 'var(--artemy-red)' : sig.severity === 'Medium' ? 'var(--artemy-yellow)' : 'var(--artemy-green)';
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: c, flexShrink: 0 }} />
                <span style={{ color: 'var(--artemy-text-secondary)' }}>{sig.title} — <strong style={{ color: 'var(--artemy-text-primary)' }}>{sig.team}</strong></span>
              </div>
            );
          }) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--artemy-green)', flexShrink: 0 }} />
              <span style={{ color: 'var(--artemy-text-secondary)' }}>Astra auto-resolved <strong style={{ color: 'var(--artemy-text-primary)' }}>17 ticket issues</strong></span>
            </div>
          )}
        </div>
      </div>

      {/* Hero grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '310px 1fr', gap: 14, marginBottom: 14 }}>
        {/* Confidence card */}
        <div style={{ background: 'var(--artemy-bg-card)', border: '1px solid var(--artemy-border)', borderRadius: 14, padding: 22, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: 'radial-gradient(circle,rgba(0,212,170,.07) 0%,transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--artemy-text-dim)', marginBottom: 12 }}>
            Portfolio Delivery Confidence
          </div>
          <ConfidenceRing score={confidenceScore} />
          <div style={{ borderTop: '1px solid var(--artemy-border)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <DimBar label="DORA Health" pct={dora?.health || 88} color="var(--artemy-green)" />
            <DimBar label="Intent Alignment" pct={74} color="var(--artemy-yellow)" />
            <DimBar label="Dependency Risk" pct={62} color="var(--artemy-red)" />
            <DimBar label="Agent Reliability" pct={95} color="var(--artemy-green)" />
            <DimBar label="Capacity Util." pct={81} color="var(--artemy-accent)" />
          </div>
        </div>

        {/* Metrics Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
          {/* Epics Card */}
          <div className="artemy-metric-card" style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-secondary)', letterSpacing: '.3px', marginBottom: 8 }}>Epics</div>
            <div style={{ flex: 1, minHeight: 60, marginTop: 5 }}>
              <EpicsCountGraph />
            </div>
          </div>

          {/* Stories */}
          <div className="artemy-metric-card" style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-secondary)', letterSpacing: '.3px' }}>User Stories</div>
            {metricsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}><CircularProgress size={20} sx={{ color: 'var(--artemy-accent)' }} /></Box>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 28, fontWeight: 700, color: 'var(--artemy-text-primary)', lineHeight: 1 }}>{metrics.stories.completed}</div>
                  <div style={{ fontSize: 12, color: 'var(--artemy-green)' }}>Done</div>
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--artemy-text-secondary)', borderTop: '1px solid var(--artemy-border)', paddingTop: 8, marginTop: 'auto' }}>
                  <div>{metrics.stories.open} To Do</div>
                  <div>{metrics.stories.unassigned} Unassigned</div>
                </div>
              </>
            )}
          </div>

          {/* Defects */}
          <div className="artemy-metric-card" style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-secondary)', letterSpacing: '.3px' }}>Defects</div>
            {metricsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}><CircularProgress size={20} sx={{ color: 'var(--artemy-accent)' }} /></Box>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 28, fontWeight: 700, color: 'var(--artemy-red)', lineHeight: 1 }}>{metrics.defects.open}</div>
                  <div style={{ fontSize: 12, color: 'var(--artemy-red)' }}>Open</div>
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--artemy-text-secondary)', borderTop: '1px solid var(--artemy-border)', paddingTop: 8, marginTop: 'auto' }}>
                  <div>{metrics.defects.completed} Resolved</div>
                  <div>{metrics.defects.unassigned} Unassigned</div>
                </div>
              </>
            )}
          </div>

          {/* Tasks */}
          <div className="artemy-metric-card" style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-secondary)', letterSpacing: '.3px' }}>Pod Tasks</div>
            {metricsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}><CircularProgress size={20} sx={{ color: 'var(--artemy-accent)' }} /></Box>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 28, fontWeight: 700, color: 'var(--artemy-accent)', lineHeight: 1 }}>{metrics.tasks.completed}</div>
                  <div style={{ fontSize: 12, color: 'var(--artemy-accent)' }}>Complete</div>
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--artemy-text-secondary)', borderTop: '1px solid var(--artemy-border)', paddingTop: 8, marginTop: 'auto' }}>
                  <div>{metrics.tasks.open} Open</div>
                </div>
              </>
            )}
          </div>

          {/* Pull Requests */}
          <div className="artemy-metric-card" style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-secondary)', letterSpacing: '.3px' }}>Pull Requests</div>
            {metricsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}><CircularProgress size={20} sx={{ color: 'var(--artemy-accent)' }} /></Box>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 28, fontWeight: 700, color: 'var(--artemy-purple)', lineHeight: 1 }}>{metrics?.pullRequests?.open || 0}</div>
                  <div style={{ fontSize: 12, color: 'var(--artemy-purple)' }}>Open</div>
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--artemy-text-secondary)', borderTop: '1px solid var(--artemy-border)', paddingTop: 8, marginTop: 'auto' }}>
                  <div>{metrics?.pullRequests?.merged || 0} Merged</div>
                </div>
              </>
            )}
          </div>

          {/* Pending Approvals */}
          <div className="artemy-metric-card" style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-secondary)', letterSpacing: '.3px' }}>Pending Approvals</div>
            {metricsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}><CircularProgress size={20} sx={{ color: 'var(--artemy-accent)' }} /></Box>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                  <StatusRing color="var(--artemy-yellow)" size={24} stroke={5} />
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 28, fontWeight: 700, color: 'var(--artemy-yellow)', lineHeight: 1 }}>{metrics?.approvals?.pending || 0}</div>
                    <div style={{ fontSize: 13, color: 'var(--artemy-yellow)', fontWeight: 600 }}>Pending</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--artemy-text-secondary)', borderTop: '1px solid var(--artemy-border)', paddingTop: 8, marginTop: 'auto' }}>
                  <div><span style={{ color: 'var(--artemy-text-primary)', fontWeight: 600 }}>{metrics?.approvals?.approved || 0}</span> Approved</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bottom grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        {/* Risks */}
        <div className="artemy-card">
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--artemy-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              ⚠ Active Risks
              <span style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 11, color: 'var(--artemy-text-dim)', background: 'var(--artemy-bg-elevated)', padding: '2px 8px', borderRadius: 10 }}>
                {risks.length} detected
              </span>
            </div>
            <span onClick={() => history.push('/control-plane/risks')} style={{ fontSize: 12, color: 'var(--artemy-accent)', cursor: 'pointer', opacity: .8 }}>View all →</span>
          </div>
          {risksLoading ? (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} sx={{ color: 'var(--artemy-accent)' }} /></Box>
          ) : topRisks.map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '28px 1fr auto', alignItems: 'center', gap: 11, padding: '10px 20px', borderBottom: '1px solid rgba(255,255,255,.03)', cursor: 'pointer' }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, background: r.severity === 'Critical' ? 'rgba(255,77,106,.12)' : 'rgba(245,166,35,.12)' }}>
                {r.severity === 'Critical' ? '🔴' : '🟡'}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</div>
                <div style={{ fontSize: 11, color: 'var(--artemy-text-dim)', fontFamily: 'var(--artemy-font-mono)' }}>{r.team} · {r.detected_at || '4h ago'}</div>
              </div>
              <SeverityChip severity={r.severity} />
            </div>
          ))}
        </div>

        {/* Pod Confidence */}
        <div className="artemy-card">
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--artemy-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 14, fontWeight: 600 }}>👥 Pod Confidence</div>
            <span onClick={() => history.push('/control-plane/dora')} style={{ fontSize: 12, color: 'var(--artemy-accent)', cursor: 'pointer', opacity: .8 }}>Details →</span>
          </div>
          <div style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { name: 'Pod Atlas', pct: 91, color: 'var(--artemy-green)' },
              { name: 'Pod Helios', pct: 58, color: 'var(--artemy-red)' },
              { name: 'Pod Orion', pct: 72, color: 'var(--artemy-yellow)' },
              { name: 'Pod Nova', pct: 87, color: 'var(--artemy-green)' },
              { name: 'Platform Core', pct: 83, color: 'var(--artemy-accent)' },
            ].map((t) => (
              <div key={t.name}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{t.name}</div>
                  <span style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 13, fontWeight: 500, color: t.color }}>{t.pct}%</span>
                </div>
                <div style={{ width: '100%', height: 4, background: 'var(--artemy-bg-elevated)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${t.pct}%`, background: t.color, borderRadius: 4 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Agent Pods Grid */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContents: 'space-between', marginBottom: 12 }}>
        <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10 }}>
          Astra Agent Pods
          <span style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10, color: 'var(--artemy-text-dim)', background: 'var(--artemy-bg-elevated)', padding: '2px 9px', borderRadius: 10, fontWeight: 400 }}>38 active</span>
        </div>
        <span onClick={() => history.push('/control-plane/pods')} style={{ fontSize: 12, color: 'var(--artemy-accent)', cursor: 'pointer', opacity: .8, marginLeft: 'auto' }}>Manage →</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {[
          { id: 'pod-ops', pod: 'OPERATIONAL', name: 'Astra Operational', color: 'var(--artemy-accent)', stats: [['247', 'Standups'], ['1.2k', 'Tickets']] },
          { id: 'pod-product', pod: 'PRODUCT', name: 'Astra Product', color: 'var(--artemy-green)', stats: [['18', 'PRDs'], ['3', 'Drifts']] },
          { id: 'pod-eng', pod: 'ENGINEERING', name: 'Astra Engineering', color: 'var(--artemy-yellow)', stats: [['312', 'PRs'], ['98%', 'Conf.']] },
          { id: 'pod-mgmt', pod: 'MANAGEMENT', name: 'Astra Executive', color: 'var(--artemy-purple)', stats: [['5', 'Briefs'], ['100%', 'Audit']] },
        ].map((p) => (
          <div key={p.id} className="artemy-card" onClick={() => history.push(`/control-plane/${p.id}`)} style={{ padding: 16, cursor: 'pointer', position: 'relative' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${p.color}, transparent)` }} />
            <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 9.5, letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: 7, color: p.color }}>{p.pod}</div>
            <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 14, fontWeight: 600, marginBottom: 5 }}>{p.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--artemy-text-secondary)', marginBottom: 10 }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--artemy-green)' }} /> 12 agents
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
              {p.stats.map(([v, l]) => (
                <div key={l} style={{ background: 'var(--artemy-bg-elevated)', borderRadius: 8, padding: '7px 9px' }}>
                  <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 15, fontWeight: 500, lineHeight: 1, marginBottom: 2, color: p.color }}>{v}</div>
                  <div style={{ fontSize: 10, color: 'var(--artemy-text-dim)' }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Box sx={{ height: 40 }} />
    </div>
  );
}
